// Tile geometry, all in unscaled px (what is saved; multiplied by SCALE when drawn): the tile and grid sizes,
// tile sizes in grid cells (sizeOf), the screen and its safe areas, clamp() (on the screen),
// place() (the nearest free spot), layout() (where every tile is drawn: adaptiveLayout(), viewportLayout() or
// autoLayout()), what each screen size keeps under the Viewport layout (band, spotOf, moveTo, spotFields, pinDrawn, makeRoom, freeOnLarge), the orders
// tiles sort in (ORDERS, autoOrder) and snap().
import { WIDGETS } from '../widgets/index.js';
import { SCALE } from '../widgets/kit.js';
import { byId, $webtop, webtop } from './state.js';
import { isGroup, onWebtop } from './tiles.js';
import { isSize } from '/model.js';

export const TILE = { w: 84, h: 92 };
// UI scale (--s in style.css). Tile positions and all the geometry below (TILE, GRID, EDGE, ...) are in unscaled
// px, which is also what is saved; they are multiplied by SCALE when drawn and divided by it when measured from
// the screen (pointer coordinates, element rects, the webtop's size). SCALE comes from widgets/kit.js.
export const unscaled = (r) => ({ left: r.left / SCALE, right: r.right / SCALE, top: r.top / SCALE, bottom: r.bottom / SCALE, width: r.width / SCALE });
// The page itself scrolls down when the tiles do not fit the screen (so iOS scrolls back to the top on a tap of the
// status bar, and the browser's own pull to refresh works); positions are in that scrolled space. The screen is
// measured on the root element, whose size holds while iOS Safari's toolbars collapse and expand during a scroll.
export const $root = document.documentElement;
// While an overlay is open (client/lib/overlay.js) the page scrolls the overlay, and the webtop's own position is held here,
// so scrolled() still answers in the webtop's space (a tile dragged out of the group panel lands where it is dropped).
let held = null;
export const scrolled = () => (held ?? scrollY) / SCALE;
export function holdScroll() { held = scrollY; }
export function releaseScroll() {
  const y = held;
  held = null;
  if (y !== null) scrollTo(0, y);
}
// Under Grid the screen holds a fixed number of columns, by screen size (COLUMNS), stretched to span it exactly, so a full
// row always meets the right edge (fitTiles, run by layout()). All the geometry stays in the same unscaled px, in a space
// fit times as wide as the screen, so saved spots and the grid never change: only drawing (placeTile, drawnSize) and a
// pointer's x (pageX) apply it, and the safe areas' sides and screenW() are in that space.
let fit = 1;
export const fitX = () => fit;
export const pageX = (clientX) => clientX / SCALE / fit;
export const pageY = (clientY) => clientY / SCALE + scrolled(); // a pointer's y on the (scrolled) webtop
export const screenW = () => $root.clientWidth / SCALE / fit;
export const screenH = () => $root.clientHeight / SCALE;
// Unscaled px to CSS px on whole device pixels: at a UI scale like 1.25 a tile would otherwise sit (and be sized) on
// fractions of a pixel, which blurs its text and lets the browser leave a sliver of an old paint (a selection
// ring, a changed figure) at its edge until something else repaints it.
export const devicePx = (v) => Math.round(v * SCALE * devicePixelRatio) / devicePixelRatio;
// The element keeps the exact position it was given (el.pos), since the rounded CSS one reads back a fraction off
// (219 at 1.25 on a 2x screen is drawn at 274 px, which is 219.2), and a tile that far off collides with its neighbour.
export function placeTile(el, p) {
  el.pos = { x: p.x, y: p.y };
  el.style.left = `${devicePx(p.x * fit)}px`;
  el.style.top = `${devicePx(p.y)}px`;
}
export const tilePos = (el) => ({ ...(el.pos ?? { x: parseFloat(el.style.left) / SCALE / fit, y: parseFloat(el.style.top) / SCALE }) });
export const EDGE = 8; // tiles always keep this far from the webtop's edges
// Tiles always keep at least TILE_GAP px between them: the snap grid leaves exactly that between cells, and the
// layout moves any tile closer than that to another (collides).
export const TILE_GAP = 6;
export const GRID = { w: TILE.w + TILE_GAP, h: TILE.h + TILE_GAP, ox: EDGE, oy: EDGE }; // snap cells, from the edge margin
export const reading = (a, b) => (a.y - b.y) || (a.x - b.x); // sort tiles in reading order of their saved spots
export const snapOn = () => Boolean(webtop.settings?.snap);
export const autoOn = () => Boolean(webtop.settings?.autoArrange);
// The Viewport switch: each screen size keeps its own tile sizes, and, in the Viewport layout, its own spots. Auto
// Arrange keeps the sizes but no spots, since it packs the tiles itself.
export const sizesPerScreen = () => Boolean(webtop?.settings?.viewport);
export const viewportOn = () => sizesPerScreen() && !webtop.settings.autoArrange;
// The screen size, by the window's width in CSS px, not unscaled: at a UI scale of 1.25 an ordinary laptop's 1440 would
// otherwise never reach large. An open sidebar is an inset and not part of this, so opening one never changes the
// layout in force. The large screen's spots and sizes are the tile's own (LAYOUT_BANDS, model.js).
const BAND_MEDIUM = 600;
const BAND_WIDE = 1000;
const BAND_LARGE = 1280;
const bandNow = () => { const w = $root.clientWidth; return w < BAND_MEDIUM ? 's' : w < BAND_WIDE ? 'm' : w < BAND_LARGE ? 'w' : 'l'; };
export const band = () => measured?.band ?? bandNow(); // read once per layout (measuring): sizeKey asks for every tile
// Multiples of three, so tiles three wide fill every row, and one or two wide most of them.
export const COLUMNS = { s: 3, m: 6, w: 9, l: 12 };
const bandEntry = (s) => (viewportOn() ? s?.layouts?.[band()] : undefined);
// Whether this tile has been placed for this screen; outside the Viewport layout every tile has its own spot.
export const hasSpot = (s) => !viewportOn() || band() === 'l' || Number.isFinite(bandEntry(s)?.x);
// A tile's saved spot on this screen, or its own when it has none yet.
export function spotOf(s) {
  const own = bandEntry(s);
  return Number.isFinite(own?.x) ? { x: own.x, y: own.y } : { x: s.x, y: s.y };
}
// Saves spot p on the tile for this screen, and answers its entry for PATCH .../shortcuts.
export function moveTo(s, p) {
  const b = viewportOn() ? band() : 'l';
  if (b === 'l') {
    Object.assign(s, { x: p.x, y: p.y });
    return { id: s.id, x: p.x, y: p.y };
  }
  s.layouts = { ...s.layouts, [b]: { ...s.layouts?.[b], x: p.x, y: p.y } };
  return { id: s.id, x: p.x, y: p.y, band: b };
}
// A new tile's fields for spot p: its own x and y, and its spot on this screen too. A copy (`from`) keeps the sizes
// the original has per screen but none of its spots, which would land it on the original on those screens.
export function spotFields(p, from) {
  const layouts = {};
  for (const [b, e] of Object.entries(from?.layouts ?? {})) if (e?.size) layouts[b] = { size: e.size };
  const b = viewportOn() ? band() : 'l';
  if (b !== 'l') layouts[b] = { ...layouts[b], x: p.x, y: p.y };
  return { x: p.x, y: p.y, layouts };
}

// The device's safe-area insets in px (all 0 in a computer's browser). A hidden probe carries them as padding,
// because env() inside a custom property is not resolved to px when read back.
const $safeProbe = Object.assign(document.createElement('div'), { id: 'safe-probe' });
export function safeArea() {
  if (measured) return measured.sa;
  const cs = getComputedStyle($safeProbe);
  const v = (k) => (parseFloat(cs[k]) || 0) / SCALE;
  return { top: v('paddingTop'), right: v('paddingRight') / fit, bottom: v('paddingBottom'), left: v('paddingLeft') / fit };
}
// What clamp() reads from the page (the safe areas, the screen, the screen size) is read once per layout() and kept here
// while it runs: clamp() runs for every spot tried for every tile, and each reading made the browser compute styles
// again, which with hundreds of tiles made a resize take seconds.
let measured = null;
function measuring(fn) {
  if (measured) return fn();
  measured = { sa: safeArea(), w: screenW(), h: screenH(), band: bandNow() };
  try { return fn(); } finally { measured = null; }
}
const screenSize = () => measured ?? { w: screenW(), h: screenH() };
// Every tile has a size: shortcuts are TILE, widgets declare their own in WIDGETS. All geometry takes it.
// Tile sizes are in grid cells, written width x height: 1x1 is a shortcut-sized tile, 2x1 two cells wide and
// one tall, 3x2 three wide and two tall. A size covers its cells and the gaps between them, so resized tiles stay on the snap grid.
// Which sizes a tile offers depends on its kind (the first listed is the default), but it may hold any other whole number
// of cells (isSize, model.js: dragging a corner, client/lib/resize.js).
export const cellsOf = (key) => key.split('x').map(Number);
export const sizeLabel = (key) => key.replace('x', '×');
const SHORTCUT_SIZES = ['1x1', '2x2'];
const GROUP_SIZES = ['1x1', '2x1', '3x1']; // wider groups preview more of their members
// A widget kind may size each tile on its own (sizesOf: an applet widget's sizes are its registration's).
export const sizesFor = (s) => WIDGETS[s?.kind]?.sizesOf?.(s) || WIDGETS[s?.kind]?.sizes || (isGroup(s) ? GROUP_SIZES : SHORTCUT_SIZES);
export function sizeKey(s) {
  const sizes = sizesFor(s);
  const own = sizesPerScreen() ? s?.layouts?.[band()]?.size : undefined;
  return [own, s?.size].find((k) => isSize(k)) ?? sizes[0];
}
// Gives the tile a size: this screen's with the Viewport switch on (the large screen's is the tile's own), else its own.
export function setSizeKey(s, key) {
  const b = sizesPerScreen() ? band() : 'l';
  if (b === 'l') s.size = key;
  else s.layouts = { ...s.layouts, [b]: { ...s.layouts?.[b], size: key } };
}
// A widget wider than the screen (a 6x3 chart in a narrow window) is drawn as wide as the screen allows instead of
// running off its edge: display only, its size stays what was picked, so it widens again when the window does.
// The room is measured once per screen width, since sizeOf runs for every tile in every layout.
let room = { at: -1, w: Infinity };
function roomW() {
  if (room.at !== $root.clientWidth || room.fit !== fit) {
    const sa = safeArea();
    room = { at: $root.clientWidth, fit, w: screenW() - 2 * EDGE - sa.left - sa.right };
  }
  return room.w;
}
export function sizeOf(s) { return sizeIn(s, sizeKey(s)); }
// What tile s measures at size `key`.
export function sizeIn(s, key) {
  const [cols, rows] = cellsOf(key);
  const w = cols * GRID.w - (GRID.w - TILE.w);
  return { w: WIDGETS[s?.kind] ? Math.max(TILE.w, Math.min(w, roomW())) : w, h: rows * GRID.h - (GRID.h - TILE.h) };
}
// What a tile measures on the page, for whatever draws inside it (a widget's figures, a chart).
export function drawnSize(s) {
  const { w, h } = sizeOf(s);
  return { w: w * fit, h };
}
// How wide the columns are drawn for this screen: this screen size's number of them, across it.
function fitTiles() {
  fit = 1;
  if (!snapOn() || autoOn()) return;
  const sa = safeArea();
  fit = (screenW() - sa.left - sa.right) / (COLUMNS[band()] * GRID.w - TILE_GAP + 2 * EDGE);
}
// Keeps a tile on the screen, clear of the webtop's edges (EDGE). The notch only floats over the tiles: the page
// scrolls far enough to bring one out from behind it (scrollEnd). `mode` 'view' keeps it on the part of the webtop
// visible now, which differs from the first screen once it has scrolled (dragging); 'open' drops the bottom edge, for
// spots below the screen when the screen is full.
export function clamp(x, y, size = TILE, mode = 'screen') {
  const sa = safeArea();
  const st = mode === 'view' ? scrolled() : 0;
  const { w: screenWidth, h: screen } = screenSize();
  const top = st + EDGE + sa.top;
  x = Math.round(Math.max(EDGE + sa.left, Math.min(x, screenWidth - size.w - EDGE - sa.right)));
  y = Math.round(Math.max(top, mode === 'open' ? y : Math.min(y, st + screen - size.h - EDGE - sa.bottom)));
  return { x, y };
}
// p and q are rects {x, y, w, h}; they collide when closer than TILE_GAP.
const near = (p, q) => p.x < q.x + q.w + TILE_GAP && q.x < p.x + p.w + TILE_GAP
  && p.y < q.y + q.h + TILE_GAP && q.y < p.y + p.h + TILE_GAP;
// Rects bucketed by grid cell, so a collision test only looks at the tiles around a spot instead of at every tile:
// layout() tries many spots for every tile it moves, and with hundreds of tiles (an imported bookmarks file)
// comparing each with all the others made a resize crawl. A rect is filed under every cell it covers and looked
// up from every cell within TILE_GAP of the spot, so no tile that collides is missed.
export class Rects {
  constructor(list = []) {
    this.cells = new Map();
    for (const r of list) this.add(r);
  }
  *keys(r, pad) {
    const x1 = Math.floor((r.x + r.w + pad) / GRID.w), y1 = Math.floor((r.y + r.h + pad) / GRID.h);
    for (let i = Math.floor((r.x - pad) / GRID.w); i <= x1; i++) {
      for (let j = Math.floor((r.y - pad) / GRID.h); j <= y1; j++) yield `${i},${j}`;
    }
  }
  add(r) {
    for (const k of this.keys(r, 0)) {
      if (!this.cells.has(k)) this.cells.set(k, new Set());
      this.cells.get(k).add(r);
    }
  }
  delete(r) {
    for (const k of this.keys(r, 0)) this.cells.get(k)?.delete(r);
  }
  // The rects p collides with.
  hits(p) {
    const found = new Set();
    for (const k of this.keys(p, TILE_GAP)) for (const q of this.cells.get(k) ?? []) if (near(p, q)) found.add(q);
    return [...found];
  }
  collides(p) {
    for (const k of this.keys(p, TILE_GAP)) for (const q of this.cells.get(k) ?? []) if (near(p, q)) return true;
    return false;
  }
}
// `taken` is a list of rects or a Rects.
const collides = (p, taken) => (taken instanceof Rects ? taken.collides(p) : taken.some((q) => near(p, q)));
const hitsOf = (p, taken) => (taken instanceof Rects ? taken.hits(p) : taken.filter((q) => near(p, q)));
// Where a tile goes when it is wanted at `at`: there if that is on the screen and clear of every rect in `taken`.
// Otherwise it keeps to its own axes first, the nearest free grid spot along its row, then along its column; then
// it looks further left, column by column (nearest row first), then to the right. Only when the screen has no room
// does it go below it, where the webtop scrolls. With `nudge` (a drop off the grid), a tile that only just overlaps
// another first slides clear of it, flush against it (above, below or beside, whichever is nearest), when that is
// less than a grid cell away: the edges of a bare widget are not drawn, so a drop that looks free often overlaps by
// a few pixels, and it should not jump to another row or column for that. With `stack` (widgets), a tile that finds no room on its row fills
// the columns from the left instead (nearest row first), so displaced widgets line up down the left edge, one
// under another, rather than in whatever column the screen edge happened to leave them.
export function place(at, taken, size = TILE, stack = false, flowFrom = null, nudge = false) {
  const p = clamp(at.x, at.y, size);
  const fits = (q) => !collides({ ...q, ...size }, taken);
  // The spot asked for is kept as it is (on the grid or not) when it fits; a tile pushed in from an edge keeps the
  // spot the edge left it, and the search below runs along its own row and column.
  const pushed = { x: p.x !== Math.round(at.x), y: p.y !== Math.round(at.y) };
  if (fits(p)) return p;
  if (nudge && !flowFrom) {
    const near = clear(p, taken, size, fits);
    if (near) return near;
  }
  const xs = columns(size), ys = [];
  for (let y = GRID.oy; y + size.h <= screenH() - EDGE - safeArea().bottom; y += GRID.h) ys.push(y);
  const nearest = (v, list) => [...list].sort((a, b) => Math.abs(a - v) - Math.abs(b - v) || a - b); // ties: left / up
  const first = (spots) => {
    for (const c of spots) {
      const q = clamp(c.x, c.y, size);
      if (fits(q)) return q;
    }
  };
  // The tile's own row and column: for an axis an edge pushed the tile along, the grid line just inside that edge;
  // otherwise its own line.
  const gridLine = (v, list, wasPushed) => (wasPushed ? list.filter((g) => g <= v).at(-1) : v) ?? v;
  const ax = gridLine(p.x, xs, pushed.x);
  const ay = gridLine(p.y, ys, pushed.y);
  const column = (x) => nearest(ay, ys).map((y) => ({ x, y }));
  if (flowFrom) { // reading-order flow (layout): the first free grid spot from flowFrom on, row by row, left to right
    const ahead = ys.filter((y) => y >= flowFrom.y)
      .flatMap((y) => xs.filter((x) => y > flowFrom.y || x >= flowFrom.x).map((x) => ({ x, y })));
    return first(ahead) || first(xs.flatMap((x) => ys.map((y) => ({ x, y }))))
      || flowBelow(flowFrom, xs, (ys.at(-1) ?? GRID.oy) + GRID.h, taken, size);
  }
  return first(nearest(ax, xs).map((x) => ({ x, y: ay })))
    || (stack && first(xs.flatMap(column)))
    || first(column(ax))
    || first(xs.filter((x) => x < ax).reverse().flatMap(column))
    || first(xs.filter((x) => x > ax).flatMap(column))
    || below({ x: stack ? GRID.ox : p.x, y: (ys.at(-1) ?? GRID.oy) + GRID.h }, taken, size);
}
// The nearest spot that clears every tile p overlaps by moving it straight up, down, left or right until it sits
// TILE_GAP from one of them, if one fits within a grid cell of p.
function clear(p, taken, size, fits, mode = 'screen') {
  const hits = hitsOf({ ...p, ...size }, taken);
  const spots = hits.flatMap((q) => [
    // rounded away from q, since clamp() rounds to whole px and a neighbour may sit on a fraction
    { x: p.x, y: Math.floor(q.y - size.h - TILE_GAP) }, { x: p.x, y: Math.ceil(q.y + q.h + TILE_GAP) },
    { x: Math.floor(q.x - size.w - TILE_GAP), y: p.y }, { x: Math.ceil(q.x + q.w + TILE_GAP), y: p.y },
  ]).map((c) => clamp(c.x, c.y, size, mode)).filter(fits);
  const dist = (c) => Math.hypot(c.x - p.x, c.y - p.y);
  const best = spots.sort((a, b) => dist(a) - dist(b))[0];
  return best && dist(best) <= Math.max(GRID.w, GRID.h) ? best : null;
}
// Below a full screen the reading-order flow goes on row by row (from flowFrom when that is already down there,
// else from the first row under the screen), so a long list of displaced tiles keeps its order there too.
function flowBelow(flowFrom, xs, under, taken, size) {
  const start = flowFrom.y >= under ? flowFrom : { x: GRID.ox, y: under };
  // Each row either takes the tile or holds at least one other, so it is found within as many rows as there are tiles.
  const rows = (taken instanceof Rects ? Infinity : taken.length) + 2;
  for (let r = 0, y = start.y; r < Math.min(rows, 10000); r++, y += GRID.h) {
    for (const x of xs) {
      if (y === start.y && x < start.x) continue;
      const q = clamp(x, y, size, 'open');
      if (!collides({ ...q, ...size }, taken)) return q;
    }
  }
  return below({ x: GRID.ox, y: under }, taken, size);
}
// The nearest free spot to `at` when the screen is full, looking outwards in grid-sized steps with no bottom edge.
function below(at, taken, size) {
  const seen = new Set();
  for (let ring = 0; ring <= 40; ring++) {
    const cands = [];
    for (let i = -ring; i <= ring; i++) {
      for (const [dx, dy] of [[i, -ring], [i, ring], [-ring, i], [ring, i]]) {
        const key = `${dx},${dy}`;
        if (seen.has(key)) continue;
        seen.add(key);
        cands.push({ ...clamp(at.x + dx * GRID.w, at.y + dy * GRID.h, size, 'open'), ...size });
      }
    }
    cands.sort((a, b) => Math.hypot(a.x - at.x, a.y - at.y) - Math.hypot(b.x - at.x, b.y - at.y));
    const free = cands.find((c) => !collides(c, taken));
    if (free) return { x: free.x, y: free.y };
  }
  return clamp(at.x, at.y, size, 'open'); // nothing free within 40 cells: overlap rather than vanish
}
// Where every tile is drawn, by one of the webtop's three layouts: the auto layout (autoLayout), while Auto Arrange is
// on, packs the tiles in its Sort By order or, Manual, the order of their saved spots (autoOrder); the Viewport layout
// (viewportLayout) draws each tile at the spot saved for this screen size; otherwise the adaptive layout
// (adaptiveLayout) adapts the saved spots to this screen, moving only the tiles that do not fit.
export function layout(first = []) {
  fitTiles();
  if (autoOn()) return autoLayout(onWebtop().sort(autoOrder()));
  return viewportOn() ? viewportLayout() : adaptiveLayout(first);
}
// The Viewport layout: nothing is moved to make room for anything. A tile is drawn exactly at the spot saved for this
// screen size, whatever its kind and however short the window (the page scrolls), or at its own spot if it has none yet
// and that is free. Where two want one place the older tile keeps it, so a new one never displaces it. What is left
// (no room at its spot, or off the side of this window, where it comes back when the window widens) goes on rows below
// the lowest tile, in reading order, display only.
function viewportLayout() {
  return measuring(() => {
    const taken = new Rects();
    const out = new Map();
    let lowest = 0;
    const put = (s, p) => {
      const size = sizeOf(s);
      taken.add({ ...p, ...size });
      out.set(s.id, p);
      lowest = Math.max(lowest, p.y + size.h);
    };
    const older = (a, b) => (a.added ?? 0) - (b.added ?? 0) || reading(spotOf(a), spotOf(b));
    const tiles = onWebtop().sort(older);
    const later = [];
    for (const s of [...tiles.filter(hasSpot), ...tiles.filter((t) => !hasSpot(t))]) {
      const at = spotOf(s), size = sizeOf(s);
      const p = clamp(at.x, at.y, size, 'open');
      if (Math.abs(p.x - at.x) < 1 && Math.abs(p.y - at.y) < 1 && !collides({ ...at, ...size }, taken)) put(s, at);
      else later.push(s);
    }
    const under = lowest ? GRID.oy + Math.ceil((lowest + TILE_GAP - GRID.oy) / GRID.h) * GRID.h : GRID.oy;
    let cursor = { x: GRID.ox, y: under };
    for (const s of later.sort((a, b) => reading(spotOf(a), spotOf(b)))) {
      const size = sizeOf(s);
      cursor = flowBelow(cursor, columns(size), under, taken, size);
      put(s, cursor);
    }
    return out;
  });
}
// The grid columns a tile of this size fits on this screen (the first alone for one wider than it).
function columns(size) {
  const right = screenSize().w - EDGE - safeArea().right;
  const xs = [];
  for (let x = GRID.ox; x + size.w <= right; x += GRID.w) xs.push(x);
  return xs.length ? xs : [GRID.ox];
}
// Room for tiles dropped on the Viewport layout's grid: rects `placed` take their spots, and `others` ({id, x, y, w, h})
// that are in the way slide forward in reading order to the next free cell. A tile that lands on the next pushes it on in
// its turn, so the slide ends at the first gap and everything past that stays. Answers the new spots of the ones that move.
export function makeRoom(placed, others) {
  return measuring(() => {
    const taken = new Rects(placed);
    const moved = new Map();
    for (const o of [...others].sort(reading)) {
      const size = { w: o.w, h: o.h };
      let p = { x: o.x, y: o.y };
      if (taken.collides({ ...p, ...size })) {
        const row = GRID.oy + Math.ceil((o.y - GRID.oy) / GRID.h) * GRID.h;
        p = flowBelow({ x: row === o.y ? o.x + 1 : GRID.ox, y: row }, columns(size), row, taken, size);
        moved.set(o.id, p);
      }
      taken.add({ ...p, ...size });
    }
    return moved;
  });
}
// The tiles drawn somewhere other than a saved spot get the spot they are drawn at, and their entries for PATCH
// .../shortcuts are answered: run whenever the user places a tile (`placed`, not to be touched), so that none of them
// shifts because of it. Not one whose spot is off the side of this window: that comes back when the window widens.
export function pinDrawn(placed = []) {
  if (!viewportOn()) return [];
  return measuring(() => [...$webtop.querySelectorAll('.tile[data-id]')].flatMap((el) => {
    const s = byId(el.dataset.id);
    if (!s || placed.includes(s)) return [];
    const at = spotOf(s), p = tilePos(el);
    const drawnThere = Math.abs(p.x - at.x) < 1 && Math.abs(p.y - at.y) < 1;
    const offSide = Math.abs(clamp(at.x, at.y, sizeOf(s), 'open').x - at.x) >= 1;
    return hasSpot(s) && (drawnThere || offSide) ? [] : [moveTo(s, p)];
  }));
}
// The first free spot for tile s among the others' own spots and sizes, on a large screen's columns, whatever the screen.
export function freeOnLarge(s) {
  const own = (t) => {
    const [cols, rows] = cellsOf(isSize(t.size) ? t.size : sizesFor(t)[0]);
    return { cols, w: cols * GRID.w - TILE_GAP, h: rows * GRID.h - TILE_GAP };
  };
  const taken = new Rects(onWebtop().filter((t) => t !== s).map((t) => { const { w, h } = own(t); return { x: t.x, y: t.y, w, h }; }));
  const { cols, w, h } = own(s);
  for (let y = GRID.oy; ; y += GRID.h) {
    for (let c = 0; c + cols <= COLUMNS.l; c++) {
      const x = GRID.ox + c * GRID.w;
      if (!taken.collides({ x, y, w, h })) return { x, y };
    }
  }
}
// Where a tile dropped at `at` lands under the Viewport layout: exactly there, far below the screen too, unless another tile is there; then the nearest free spot around it, never back up on the first screen.
export function settle(at, taken, size, nudge = false) {
  return measuring(() => {
    const p = clamp(at.x, at.y, size, 'open');
    const fits = (q) => !collides({ ...q, ...size }, taken);
    if (fits(p)) return p;
    return (nudge && clear(p, taken, size, fits, 'open')) || below(p, taken, size);
  });
}
// The adaptive layout: each tile at its saved spot when that is still on the screen, clear of the other tiles. Only the tiles that do not fit there (pushed in from an edge, or sitting on another tile) move, so a
// smaller screen leaves the rest alone. Display only; nothing is saved. Tiles listed in `first` (just placed by the
// user) keep their spots ahead of all; the rest go one at a time in reading order of their saved spots, and the
// ones that move flow in that order: each takes the first free grid spot after the previous one that moved (the
// first starts on its own row), so they keep the order they were laid out in, whatever their kind. Widgets still
// outrank shortcuts where they overlap: a widget whose saved spot fits (one dropped onto shortcuts, say) keeps it,
// and shortcuts are kept out of it.
function adaptiveLayout(first) {
  return measuring(() => {
    // Widgets only have to clear the tiles placed so far (taken); shortcuts also stay out of the saved spots of the
    // widgets not placed yet (blocked holds both, and a widget's reservation leaves it once the widget is placed).
    const taken = new Rects();
    const blocked = new Rects();
    const reservedOf = new Map();
    const out = new Map();
    const put = (s, p) => {
      const r = { ...p, ...sizeOf(s) };
      taken.add(r);
      blocked.add(r);
      if (reservedOf.has(s.id)) blocked.delete(reservedOf.get(s.id));
      out.set(s.id, p);
    };
    for (const s of first) put(s, place(s, taken, sizeOf(s)));
    const rest = onWebtop().filter((s) => !first.includes(s)).sort(reading);
    const fits = (s) => { const p = clamp(s.x, s.y, sizeOf(s)); return Math.abs(p.x - s.x) < 1 && Math.abs(p.y - s.y) < 1; };
    // The saved spots of widgets that fit the screen: shortcuts stay out of them (widgets may still take them over).
    for (const s of rest) {
      if (!WIDGETS[s.kind] || !fits(s)) continue;
      const r = { x: s.x, y: s.y, ...sizeOf(s) };
      reservedOf.set(s.id, r);
      blocked.add(r);
    }
    let cursor = null; // where the last tile that moved went
    for (const s of rest) {
      const widget = !!WIDGETS[s.kind];
      const size = sizeOf(s);
      const blockers = widget ? taken : blocked;
      if (fits(s) && !collides({ x: s.x, y: s.y, ...size }, blockers)) {
        put(s, { x: Math.round(s.x), y: Math.round(s.y) });
        continue;
      }
      const row = Math.max(GRID.oy, GRID.oy + Math.floor((clamp(s.x, s.y, size).y - GRID.oy) / GRID.h) * GRID.h);
      cursor = place(s, blockers, size, widget, cursor ?? { x: GRID.ox, y: row });
      put(s, cursor);
    }
    return out;
  });
}
// The auto layout (Auto Arrange): tiles packed on the grid from the top left in the given order, row by row like
// text, wrapping at the screen's right edge. autoLayout() is what is drawn: each tile takes the first free spot from
// the top that it fits, so a small tile fills a hole a bigger one left. orderSpots() is what is saved, only to keep
// the order: each tile goes after the one before, never back, so the order reads back from those spots exactly
// (cellOrder) on any screen width.
export const autoLayout = (list) => packGrid(list, true);
export const orderSpots = (list) => packGrid(list, false);
// The auto layout's packing for another screen size, on its number of columns.
export const packedFor = (list, columns) => packGrid(list, true, columns);
function packGrid(list, fill, columns) {
  return measuring(() => {
    const sa = safeArea();
    const x0 = GRID.ox + (columns ? 0 : sa.left), y0 = GRID.oy + (columns ? 0 : sa.top);
    const { w: width } = screenSize();
    const cols = columns ?? Math.max(1, Math.floor((width - EDGE - sa.right - x0 - TILE.w) / GRID.w) + 1);
    const at = (c, r) => ({ x: x0 + c * GRID.w, y: y0 + r * GRID.h });
    const used = new Set();
    const free = (c, r, w, h) => {
      for (let i = 0; i < w; i++) for (let j = 0; j < h; j++) if (used.has(`${c + i},${r + j}`)) return false;
      return true;
    };
    const full = (r) => { for (let i = 0; i < cols; i++) if (!used.has(`${i},${r}`)) return false; return true; };
    const pos = new Map();
    let c = 0, r = 0, top = 0; // top: the first row with a free cell, where a fill search starts
    for (const s of list) {
      const [cw, h] = cellsOf(sizeKey(s));
      const w = Math.min(cw, cols); // a widget wider than the screen is drawn narrower (sizeOf)
      if (fill) { c = 0; r = top; }
      for (;; c++) {
        if (c + w > cols) { c = 0; r++; }
        if (free(c, r, w, h)) break;
      }
      for (let i = 0; i < w; i++) for (let j = 0; j < h; j++) used.add(`${c + i},${r + j}`);
      pos.set(s.id, at(c, r));
      c += w;
      while (full(top)) top++;
    }
    return pos;
  });
}
// The order Auto Arrange keeps, read from spots {x, y}: by grid row (the nearest to the top edge), then column.
const cell = (v, o, step) => Math.round((v - o) / step);
export const rowOf = (p) => cell(p.y, GRID.oy, GRID.h);
export const cellOrder = (a, b) => rowOf(a) - rowOf(b) || cell(a.x, GRID.ox, GRID.w) - cell(b.x, GRID.ox, GRID.w);
// The orders tiles sort in (Sort By, client/lib/arrange.js); settings.sortBy is one of these keys (the server's SORT_BY).
// A collator rather than localeCompare, since the auto layout sorts on every render and resize frame.
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
// By title, A to Z; a tile may have no title (an unnamed group) and sorts as empty, ahead of the rest.
export const byTitle = (a, b) => collator.compare(a.title || '', b.title || '');
const typeRank = (s) => (WIDGETS[s.kind] ? 0 : isGroup(s) ? 1 : s.kind === 'webtop' ? 2 : 3);
const area = (s) => { const [w, h] = cellsOf(sizeKey(s)); return w * h; };
export const ORDERS = {
  manual: cellOrder, // the order of the saved spots (order spots, under Auto Arrange)
  name: byTitle,
  // Widgets, groups, webtop links, then links; A to Z within each.
  type: (a, b) => typeRank(a) - typeRank(b) || byTitle(a, b),
  // Biggest first, so the big tiles pack together and the small ones fill in after them; A to Z within a size.
  size: (a, b) => area(b) - area(a) || byTitle(a, b),
  // Oldest first; tiles from before the date was kept have 0, so they stay first, in the order of their spots.
  added: (a, b) => (a.added ?? 0) - (b.added ?? 0) || reading(a, b),
};
export const sortBy = () => webtop.settings?.sortBy || 'manual';
// The order the auto layout packs tiles in: the Sort By choice, or by hand (Manual) the order of their spots.
export const autoOrder = () => ORDERS[sortBy()] ?? cellOrder;

// Snaps to the grid when the webtop's Snap to Grid is on, or when `force` is set (Shift held while dragging).
// `view` keeps it on the visible part of a scrolled webtop (see clamp); under the Viewport layout a tile goes anywhere
// below the top edge instead.
export function snap(x, y, size = TILE, force = false, view = false) {
  const mode = view ? (viewportOn() ? 'open' : 'view') : 'screen';
  if (!snapOn() && !force) return clamp(x, y, size, mode);
  const sx = GRID.ox + Math.round((x - GRID.ox) / GRID.w) * GRID.w;
  const sy = GRID.oy + Math.round((y - GRID.oy) / GRID.h) * GRID.h;
  return clamp(sx, sy, size, mode);
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  document.body.appendChild($safeProbe);
}
